import { Button, Container, Typography } from '@cleartrip/ct-design-components'
import { TrendingDownIcon, TrendingUpIcon } from './icons'
import {
    ACCEPT_NEW_FARE_CTA,
    FIND_ANOTHER_FLIGHT_CTA,
    NEW_FLIGHT_PRICE_LABEL,
    PRICE_CHANGE_ALERT_TITLE,
    PRICE_CHANGE_ICON_COLORS,
    PRICE_CHANGE_VARIANT,
    PriceChangeVariant
} from './constants'

export type PriceChangeAlertProps = {
    /** PRICE_CHANGE_VARIANT.DROP -> price got cheaper (green), .INCREASE -> price went up (red) */
    variant: PriceChangeVariant
    /** Formatted new price, e.g. "₹7,409" */
    newPrice: string
    /** Formatted delta with sign, e.g. "(- ₹409)" / "(+ ₹409)" */
    priceDelta: string
    /** Optional BE title override; falls back to Figma copy */
    title?: string
    onAcceptNewFare?: () => void
    onFindAnotherFlight?: () => void
}

// Figma: node-id 3306-35075 (Down/price drop) & 3306-35483 (Up/price increase)
const PriceChangeAlert = ({
    variant,
    newPrice,
    priceDelta,
    title,
    onAcceptNewFare = () => {},
    onFindAnotherFlight = () => {}
}: PriceChangeAlertProps) => {
    const isDrop = variant === PRICE_CHANGE_VARIANT.DROP

    return (
        <Container className="flex flex-column flex-middle flex-gap-6 pt-6 pb-4 px-4 w-100p">
            <Container className="flex flex-column flex-middle flex-gap-4 w-100p">
                <Container
                    className="flex flex-middle flex-center flex-shrink-0"
                    css={{
                        width: '56px',
                        height: '56px',
                        borderRadius: '60px',
                        background: PRICE_CHANGE_ICON_COLORS[variant].background
                    }}
                >
                    {isDrop ? <TrendingDownIcon /> : <TrendingUpIcon />}
                </Container>
                {/* Figma 3306:35083 — title + price block: 8px gap, price row adds 8px top pad. */}
                <Container className="flex flex-column flex-gap-2 w-100p">
                    <Typography variant="HM2" className="ta-center w-100p">
                        {title || PRICE_CHANGE_ALERT_TITLE[variant]}
                    </Typography>
                    {/* Figma 3306:35086 — label left, amount + delta right (4px gap). */}
                    <Container
                        className="flex items-start w-100p"
                        css={{ columnGap: '4px', paddingTop: '8px' }}
                    >
                        <Container css={{ flex: '1 0 0', minWidth: 0 }}>
                            <Typography variant="P2">{NEW_FLIGHT_PRICE_LABEL}</Typography>
                        </Container>
                        <Typography variant="P2" className="ws-nowrap flex-shrink-0">
                            {newPrice}
                        </Typography>
                        {priceDelta ? (
                            <Typography
                                variant="P2"
                                color={isDrop ? 'success' : 'warning'}
                                className="ws-nowrap flex-shrink-0"
                            >
                                {priceDelta}
                            </Typography>
                        ) : null}
                    </Container>
                </Container>
            </Container>
            <Container className="flex flex-middle w-100p" css={{ columnGap: '16px' }}>
                <Container css={{ flex: '1 1 0', minWidth: 0 }}>
                    {/* Figma 3306:35098 — Size=40px (tertiary-regular) → medium */}
                    <Button variant="outline" size="medium" isFullWidth onClick={onFindAnotherFlight}>
                        {FIND_ANOTHER_FLIGHT_CTA}
                    </Button>
                </Container>
                <Container css={{ flex: '1 1 0', minWidth: 0 }}>
                    {/* Figma 3306:35099 — Size=40px black fill (secondary-regular) → medium;
                        B2C `primary` contained maps to neutral900 / charcoal. */}
                    <Button variant="contained" color="primary" size="medium" isFullWidth onClick={onAcceptNewFare}>
                        {ACCEPT_NEW_FARE_CTA}
                    </Button>
                </Container>
            </Container>
        </Container>
    )
}

export default PriceChangeAlert
